import { clsx } from 'clsx'
import { type FC, type ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  type OfferV11,
  PrescriptionV3PrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../../global-config'
import { TENANT } from '../../../../tenant'
import { productAccessorIsSaeOffer } from '../../../model/productAccessor/productAccessorIsSaeOffer'
import { productAccessorOfferNowProductGroupIsSameDayNextDay } from '../../../model/productAccessor/productAccessorOfferNowProductGroupIsSameDayNextDay'
import { PRODUCT_TEMPLATES } from '../../../model/productTemplates'
import { useProductPageRebrushStyle } from '../../product-page-context/useProductPageRebrushStyle'
import { useProductPageContext } from '../../productPageContext'
import { ProductPriceExplainer } from '../ProductPriceExplainer'
import { ProductPriceTransparencyIcon } from '../ProductPriceTransparencyIcon'

const getRetailOrPrescriptionPriceLabel = ({
  currentPrescriptionType,
  isAtSaeNowOffer,
  isOtc,
}: {
  currentPrescriptionType: string
  isAtSaeNowOffer: boolean
  isOtc: boolean
  superScripts: PublicGlobalConfig['superScripts']
}): ReactElement => {
  const otcOrPrivatePrescriptionMessage = isAtSaeNowOffer ? (
    <FormattedMessage id="productPricing.buyBox.shopApothekePrice" />
  ) : (
    <FormattedMessage id="general.label.yourPrice" />
  )

  const isOtcOrPrivatePrescription =
    isOtc ||
    currentPrescriptionType ===
      PrescriptionV3PrescriptionTypeEnum.PrivatePrescription

  return isOtcOrPrivatePrescription ? (
    otcOrPrivatePrescriptionMessage
  ) : (
    <FormattedMessage id="general.label.additionalCharge" />
  )
}

const getIsAtSaeNowOffer = (
  currentOffer: OfferV11 | undefined,
  tenant: TENANT,
): boolean => {
  const isSaeOffer = Boolean(
    currentOffer && productAccessorIsSaeOffer(currentOffer),
  )

  const nowEligible: boolean =
    currentOffer !== undefined &&
    productAccessorOfferNowProductGroupIsSameDayNextDay(currentOffer)

  return tenant === TENANT.AT && isSaeOffer && nowEligible
}

const getIsErxWithRecommendedPrice = (
  currentPrescriptionType: PrescriptionV3PrescriptionTypeEnum,
): boolean => {
  return (
    PrescriptionV3PrescriptionTypeEnum.InsurancePrescription ===
      currentPrescriptionType ||
    PrescriptionV3PrescriptionTypeEnum.FreePrescription ===
      currentPrescriptionType
  )
}

export const ProductPageSingleAttributesHeaderLabels: FC = () => {
  const { superScripts, tenant } = useGlobalConfigContext()
  const { currentOffer, currentPrescriptionType, productTemplate } =
    useProductPageContext()
  const isOtc =
    productTemplate === PRODUCT_TEMPLATES.OTC ||
    currentPrescriptionType ===
      PrescriptionV3PrescriptionTypeEnum.NoPrescription
  const isRebrushStyle = useProductPageRebrushStyle()
  const isErxWithRecommendedPrice: boolean = getIsErxWithRecommendedPrice(
    currentPrescriptionType,
  )
  const isAtSaeNowOffer = getIsAtSaeNowOffer(currentOffer, tenant)
  const retailOrPrescriptionPriceLabel = getRetailOrPrescriptionPriceLabel({
    currentPrescriptionType,
    isAtSaeNowOffer,
    isOtc,
    superScripts,
  })

  return (
    <div
      className={clsx(
        'flex pt-5 tablet:mx-5',
        isRebrushStyle && 'pb-1.5',
        !isRebrushStyle && 'pb-2.5',
      )}
    >
      <div className="inline-flex flex-1 basis-2/4 items-center mobile-md:mr-11 desktop:mr-0 desktop:basis-5/12">
        <ProductPriceExplainer />
      </div>
      <div
        className={clsx(
          'flex flex-1 basis-1/4 items-center justify-end whitespace-nowrap px-2 text-s font-medium text-dark-primary-strong desktop:hidden',
          isErxWithRecommendedPrice && 'flex-col text-right',
          isAtSaeNowOffer && 'flex-col items-end',
        )}
      >
        <span>{retailOrPrescriptionPriceLabel}</span>
        <ProductPriceTransparencyIcon className="ml-1" />
      </div>
      <div
        className={clsx(
          'hidden justify-end px-2 text-s font-medium text-dark-brand desktop:flex',
          isAtSaeNowOffer ? 'basis-4/12 text-right' : 'basis-6/12',
        )}
      >
        <span>{retailOrPrescriptionPriceLabel}</span>
      </div>
      <div
        className={clsx(
          'ml-2.5 flex flex-1 items-center justify-end text-s font-medium desktop:ml-0 desktop:basis-1/12 desktop:items-start',
          isErxWithRecommendedPrice && 'flex-col justify-end text-right',
          isAtSaeNowOffer ? 'text-right' : 'whitespace-nowrap',
        )}
      >
        {isOtc ? (
          <span className="text-dark-primary-max">
            <FormattedMessage id="product.details.variants.savings.label" />
          </span>
        ) : null}
      </div>
    </div>
  )
}
